import type { LivePhoto as LivePhotoDataType } from '@/@types/common'
import LivePhotoIcon from '@/assets/live-photo.svg?react'
import clsx from 'clsx'
import { motion } from 'motion/react'
import {
	type HTMLAttributes,
	type Ref,
	type SyntheticEvent,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react'
import styles from './LivePhoto.module.css'

export interface Player {
	play: () => void
	pause: () => void
	toggle: () => void
	stop: () => void
}

export interface PlayerEventHandlerProps {
	onPhotoLoad?: (player: Player, event: SyntheticEvent<HTMLImageElement, Event>) => void
	onVideoLoad?: (player: Player, event: SyntheticEvent<HTMLVideoElement, Event>) => void
	onCanPlay?: (player: Player, event: SyntheticEvent<HTMLVideoElement, Event>) => void
	onEnded?: (player: Player, event: SyntheticEvent<HTMLVideoElement, Event>) => void
	onError?: (player: Player, event: SyntheticEvent<HTMLVideoElement, Event>) => void
}

export interface LivePhotoProps
	extends
		Omit<HTMLAttributes<HTMLDivElement>, keyof PlayerEventHandlerProps>,
		PlayerEventHandlerProps {
	data: LivePhotoDataType | undefined
	width?: number
	height?: number

	isPreview?: boolean
	/**
	 * Image URL used as the cover when `isPreview` is true.
	 * Falls back to `data.imageUrl` if not provided.
	 */
	previewImageUrl?: string
	autoPlayOnce?: boolean
	ref?: Ref<LivePhotoHandle>
}

export interface LivePhotoHandle {
	player: Player | null
	root: HTMLDivElement | null
}

export default function LivePhoto({
	width,
	height,
	data,
	onPhotoLoad,
	onVideoLoad,
	onCanPlay,
	onEnded,
	onError,
	isPreview = false,
	previewImageUrl,
	autoPlayOnce = false,
	ref,
	className,
	style,
	...props
}: LivePhotoProps) {
	const [isPlaying, setIsPlaying] = useState<boolean>(false)
	const [canPlay, setCanPlay] = useState<boolean>(false)
	const imgRef = useRef<HTMLImageElement>(null)
	const videoRef = useRef<HTMLVideoElement>(null)
	const rootRef = useRef<HTMLDivElement>(null)
	const play = useCallback(() => {
		videoRef.current?.play()
	}, [])

	const pause = useCallback(() => {
		videoRef.current?.pause()
	}, [])

	const toggle = useCallback(() => {
		if (videoRef.current?.paused) {
			play()
		} else {
			pause()
		}
	}, [pause, play])

	const reset = useCallback(() => {
		if (videoRef.current) {
			videoRef.current.currentTime = 0
		}
	}, [])

	const stop = useCallback(() => {
		pause()
	}, [pause])

	const playerRef = useRef<Player>({
		play,
		pause,
		toggle,
		stop,
	})

	useImperativeHandle(ref, () => ({
		get player() {
			return playerRef.current
		},
		get root() {
			return rootRef.current
		},
	}))

	useEffect(() => {
		if (!isPreview && autoPlayOnce && canPlay) {
			play()
		}
	}, [autoPlayOnce, isPreview, canPlay])

	const containerStyle = useMemo(
		() => ({
			...(style ?? {}),
			...(!isPreview && width && height ? { aspectRatio: `${width} / ${height}` } : {}),
		}),
		[height, isPreview, style, width]
	)

	if (!data) {
		return null
	}

	return (
		<div
			ref={rootRef}
			{...props}
			className={clsx(styles.container, className)}
			style={containerStyle}
		>
			<img
				ref={imgRef}
				src={isPreview ? (previewImageUrl ?? data.imageUrl) : data.imageUrl}
				alt="LivePhoto"
				className={clsx('live-photo-cover', styles.media, isPreview && styles.mediaPreview)}
				referrerPolicy="no-referrer"
				loading="lazy"
				onLoad={(event: SyntheticEvent<HTMLImageElement, Event>) => {
					onPhotoLoad?.(playerRef.current, event)
				}}
			/>
			<video
				ref={videoRef}
				src={isPreview ? undefined : data.videoUrl}
				className={clsx(
					'live-photo-video',
					styles.video,
					isPreview && styles.videoPreview,
					isPlaying && styles.videoPlaying
				)}
				playsInline
				muted
				controls={false}
				onLoadedData={(event: SyntheticEvent<HTMLVideoElement, Event>) =>
					onVideoLoad?.(playerRef.current, event)
				}
				onCanPlay={(event: SyntheticEvent<HTMLVideoElement, Event>) => {
					setCanPlay(true)
					onCanPlay?.(playerRef.current, event)
				}}
				onEnded={(event: SyntheticEvent<HTMLVideoElement, Event>) => {
					stop()
					return onEnded?.(playerRef.current, event)
				}}
				onError={(event: SyntheticEvent<HTMLVideoElement, Event>) =>
					onError?.(playerRef.current, event)
				}
				onPlay={() => {
					setIsPlaying(true)
				}}
				onPause={() => {
					setIsPlaying(false)
				}}
			/>
			{/* live photo badge */}
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.2 }}
				onMouseOver={() => {
					play()
				}}
				onMouseLeave={() => {
					stop()
				}}
				className={styles.badge}
			>
				<LivePhotoIcon className={styles.badgeIcon} />

				<span className={styles.badgeText}>LIVE</span>
			</motion.div>
		</div>
	)
}
